import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Appointment from 'model/Appointment'
import { AppThunk } from 'store'
import AppointmentRepository from 'clients/db/AppointmentRepository'
import AppointmentsPage from 'model/ApointmentsPage'
import AppointmentsPagesRepository from 'clients/db/AppointmentsPagesRepository'
import AppointmentMonthPayload from 'model/AppointmentMonthPayload'
import AppointmentsWithPatientsRepository from 'clients/db/AppointmentsWithPatientsRepository'
import EventMonth from 'model/EventMonth'
import { getNextMonth, getTimeStampInSeconds } from 'utils/DateUtil'
import WorkingHoursRepository from 'clients/db/WorkingHoursRepository'

interface AppointmentsState {
  isLoading: boolean
  appointments: Appointment[]
  appointmentsPages: AppointmentsPage[]
  appointmentsMonths: any
  eventMonths: EventMonth[]
}

const initialState: AppointmentsState = {
  isLoading: false,
  appointments: [],
  appointmentsPages: [],
  appointmentsMonths: {},
  eventMonths: [],
}

function startLoading(state: AppointmentsState) {
  state.isLoading = true
}

const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    fetchAppointmentsStart: startLoading,
    fetchAppointmentsMonthStarted: (state, { payload }: PayloadAction<AppointmentMonthPayload>) => {
      state.isLoading = true
      const newApointmentsMonth = { ...state.appointmentsMonths }
      newApointmentsMonth[payload.month] = payload.appointments
      state.appointmentsMonths = newApointmentsMonth
    },
    fetchAppointmentsSuccess: (state, { payload }: PayloadAction<Appointment[]>) => {
      state.isLoading = false
      state.appointments = payload
    },
    fetchAppointmentsFirstPageSuccess: (state, { payload }: PayloadAction<AppointmentsPage>) => {
      state.isLoading = false
      state.appointmentsPages = [payload]
    },
    fetchAppointmentsPageSuccess: (state, { payload }: PayloadAction<AppointmentsPage>) => {
      state.isLoading = false
      state.appointmentsPages = [...state.appointmentsPages, payload]
    },
    fetchAppointmentsMonthSuccess: (state, { payload }: PayloadAction<AppointmentMonthPayload>) => {
      state.isLoading = false
      const newApointmentsMonth = { ...state.appointmentsMonths }
      newApointmentsMonth[payload.month] = payload.appointments
      state.appointmentsMonths = newApointmentsMonth
    },
    fetchAppointmentsMonthSuccessStartingFetchEvents: (
      state,
      { payload }: PayloadAction<AppointmentMonthPayload>,
    ) => {
      state.isLoading = true
      const newApointmentsMonth = { ...state.appointmentsMonths }
      newApointmentsMonth[payload.month] = payload.appointments
      state.appointmentsMonths = newApointmentsMonth
    },
    fetchEventsSuccess: (state, { payload }: PayloadAction<EventMonth>) => {
      state.isLoading = false
      const newMonthEvents = [...state.eventMonths, payload]
      state.eventMonths = newMonthEvents
    },
    clearAppointments: (state) => {
      state.appointmentsMonths = {}
      state.eventMonths = []
    },
  },
})

export const {
  fetchAppointmentsStart,
  fetchAppointmentsSuccess,
  fetchAppointmentsFirstPageSuccess,
  fetchAppointmentsPageSuccess,
  fetchAppointmentsMonthStarted,
  fetchAppointmentsMonthSuccess,
  fetchEventsSuccess,
  fetchAppointmentsMonthSuccessStartingFetchEvents,
  clearAppointments,
} = appointmentsSlice.actions

export const reloadApointments = (): AppThunk => async (dispatch) => {
  dispatch(clearAppointments())
}

/*
export const deleteDoctor = (id: any): AppThunk => async (dispatch) => {
  dispatch(clearAppointments())
}
*/

export const fetchMonthEvents = (
  userId: string,
  date: Date,
  appointmentsType: string,
  speciality: null | string,
): AppThunk => async (dispatch) => {
  const yearMonthDate = date.toISOString().slice(0, 7)
  const yearMonthTimeStamp = getTimeStampInSeconds(new Date(date.getFullYear(), date.getMonth(), 1))

  dispatch(fetchAppointmentsMonthStarted({ month: yearMonthDate, appointments: [] }))
  var nextMonth = getNextMonth(date)
  const nextYearMonth = nextMonth.toISOString().slice(0, 7)
  const nextYearMonthTimeStamp = getTimeStampInSeconds(
    new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1),
  )

  const typeSelector = appointmentsType && appointmentsType !== 'all' ? appointmentsType : {}

  const selector = {
    $and: [
      { $gt: ['$startDateTime', yearMonthDate] },
      { $lt: ['$startDateTime', nextYearMonth] },
      {
        ...(appointmentsType === 'specialist' &&
          speciality && { $eq: ['$speciality', speciality] }),
      },
      appointmentsType !== 'all' ? { $eq: ['$type', typeSelector] } : {},
    ],
  }

  const appointmentsWithPatients = await AppointmentsWithPatientsRepository.getAppointmentsWithPatients(
    selector,
  )

  const workingHoursSelector = {
    selector: {
      $and: [
        {
          startTimeStamp: { $gt: yearMonthTimeStamp },
        },
        {
          startTimeStamp: { $lt: nextYearMonthTimeStamp },
        },
        {
          userId,
        },
      ],
    },
  }
  const workingHours = await WorkingHoursRepository.getWorkingHours(workingHoursSelector)

  const newEvents =
    appointmentsWithPatients && appointmentsWithPatients.docs
      ? appointmentsWithPatients.docs.map((appointmentWithPatient) => {
          return {
            id: appointmentWithPatient.appointment.id,
            start: new Date(appointmentWithPatient.appointment.startDateTime),
            end: new Date(appointmentWithPatient.appointment.endDateTime),
            title: appointmentWithPatient.patient.fullName || '',
            allDay: false,
            extendedProps: {
              type: 'APPOINTMENT',
              patientId: appointmentWithPatient.appointment.patientId,
            },

            backgroundColor: '#A85684',
            rendering: 'auto',
          }
        })
      : []

  const workingHoursEvents = workingHours.map((workingHours) => {
    return {
      id: workingHours.id,
      start: new Date(workingHours.startTimeStamp * 1000),
      end: new Date(workingHours.endTimeStamp * 1000),
      title: '',
      allDay: false,
      extendedProps: { type: 'WORKING_HOURS', userId: workingHours.userId, patientId: '' },
      backgroundColor: '#5CA1B3',
      //backgroundColor: 'red',
      rendering: 'background',
      //rendering: 'auto',
    }
  })

  newEvents.push(...workingHoursEvents)

  const newEventMonth = { month: yearMonthDate, events: newEvents }
  dispatch(fetchEventsSuccess(newEventMonth))
}

export const fetchPatientAppointments = (
  patientId: string,
  searchString?: string,
): AppThunk => async (dispatch) => {
  dispatch(fetchAppointmentsStart())

  let appointments
  if (searchString === undefined || searchString.trim() === '') {
    const query = {
      selector: { patientId },
      sort: {
        startDateTime: -1,
      },
    }
    appointments = await AppointmentRepository.search(query)
  } else {
    appointments = await AppointmentRepository.searchPatientAppointments(patientId, searchString)
  }

  dispatch(fetchAppointmentsSuccess(appointments))
}

export const fetchPatientAppointmentsPage = (
  selector: any,
  bookmark: string,
  searchString?: string,
  limit?: number,
): AppThunk => async (dispatch) => {
  dispatch(fetchAppointmentsStart())

  let appointments
  if (searchString === undefined || searchString.trim() === '') {
    appointments = await AppointmentsPagesRepository.searchPatientAppointmentsPage(
      selector,
      bookmark,
      limit,
    )
  } else {
    appointments = await AppointmentsPagesRepository.searchPatientAppointmentsPageText(
      selector,
      bookmark,
      searchString,
      limit,
    )
  }

  if (bookmark === '') {
    dispatch(fetchAppointmentsFirstPageSuccess(appointments))
  } else {
    dispatch(fetchAppointmentsPageSuccess(appointments))
  }
}

export default appointmentsSlice.reducer
